<template>
  <div class="pb-12">
    <v-progress-linear class="section-progress-stepper" :value="progressCount" />
    <v-row class="pt-5">
      <v-col cols="12" md="6" class="pl-md-10">
        <transition-group
          enter-active-class="fadeIn"
          leave-active-class="fadeOutFast"
          mode="out-in"
        >
          <template v-for="(question, index) in questions">
            <div
              v-if="checkVisible(question)"
              :id="'question-' + question.id"
              :key="question.id"
            >
              <dropdown-question
                v-if="question.type === 'dropdown'"
                :question="question"
                :current-answer="getAnswer(question)"
                :parent-answer="getParentAnswer(question)"
                :answers="answers"
                :nudge-bottom="index === 0 ? 230 : 100"
                class="question-box mb-5 d-flex align-center justify-center"
                @selectAnswer="addAnswer($event, index)"
              />
              <template v-else-if="question.id === 2">
                <div class="question-box mb-5 pt-5 d-flex flex-column align-center justify-center">
                  <car-question-autocomplete
                    :question="question.items[0]"
                    class="mb-3"
                    @updateAnswer="addAnswer($event, index)"
                  />
                  <text-question
                    :question="question.items[1]"
                    @updateAnswer="addAnswer($event, index)"
                  />
                </div>
              </template>
              <text-question
                v-else-if="question.type === 'text'"
                :question="question"
                class="question-box mb-5 d-flex align-center justify-center"
                @updateAnswer="addAnswer($event, index)"
              />
              <radio-question
                v-else-if="question.type === 'radio'"
                :question="question"
                :current-answer="getAnswer(question)"
                class="question-box mb-5"
                @selectAnswer="addAnswer($event, index)"
              />
              <slider-question
                v-else-if="question.type === 'slider'"
                :question="question"
                :current-answer="getAnswer(question)"
                :parent-answer="getParentAnswer(question)"
                class="question-box mb-5 d-flex align-center justify-center"
                @selectAnswer="addAnswer($event, index)"
              />
            </div>
          </template>
        </transition-group>
      </v-col>
      <v-col cols="12" md="6" class="d-flex align-center justify-center">
        <transition
          enter-active-class="fadeIn"
          leave-active-class="fadeOut"
          mode="out-in"
        >
          <div
            v-if="completedSection"
            id="confirmation-section"
            :key="'confirmation-section'"
            class="d-flex flex-column justify-center align-center confirmation-section"
          >
            <complete-contact-form
              class="question-box pa-5"
              @updateData="updateContactData"
            />
            <div class="mt-5">
              <transition
                enter-active-class="fadeIn"
                leave-active-class="fadeOut"
                mode="out-in"
              >
                <v-btn
                  v-if="validContactInfo"
                  color="#df0100"
                  class="base-button"
                  depressed
                  tile
                  dark
                  large
                  @click="confirmCasco"
                >
                  Solicită oferta
                  <v-icon right>
                    mdi-arrow-right-circle
                  </v-icon>
                </v-btn>
                <div v-else style="height: 44px"></div>
              </transition>
            </div>
          </div>
        </transition>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { CONSTANTS, getQuestions } from '@/_utils/static-data';
import DropdownQuestion from '@/components/DropdownQuestion';
import CarQuestionAutocomplete from '@/components/casco/CarQuestionAutocomplete';
import TextQuestion from '@/components/TextQuestion';
import RadioQuestion from '@/components/RadioQuestion';
import SliderQuestion from '@/components/casco/SliderQuestion';
import CompleteContactForm from '@/components/casco/CompleteContactForm';

export default {
  name: 'CascoCalculator',
  components: { CompleteContactForm, SliderQuestion, RadioQuestion, TextQuestion, CarQuestionAutocomplete, DropdownQuestion },
  data() {
    return {
      answers: [],
      lastAnswerIndex: 1,
      contactInfo: [],
      validContactInfo: false
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.width < 960;
    },
    questions() {
      const allQuestions = getQuestions('casco', 'calculator');
      let visibleQuestions = [];
      for (const question of allQuestions) {
        if (!question.parent) {
          visibleQuestions.push(question);
        } else if (question.parent && question.visibleIf) {
          const visibleIfParent = this.answers.find(el => el.questionId === question.visibleIf.questionId);
          if (visibleIfParent?.id === question.visibleIf.answer) {
            const parentAnswer = this.answers.find(el => el.questionId === question.parent);
            const questionOptions = question.filterable
              ? question.options.filter(el => el.parentId === parentAnswer?.id || !el.parentId)
              : question.options;
            if (questionOptions.length > 0) {
              visibleQuestions.push(question);
            }
          }
        } else if (question.parent && !question.visibleIf) {
          const parentAnswer = this.answers.find(el => el.questionId === question.parent);
          if (parentAnswer) {
            visibleQuestions.push(question);
          }
        }
      }
      return visibleQuestions;
    },
    totalQuestions() {
      let total = 0;
      this.questions.map((el) => {
        if (el.type === 'multiple-questions') {
          total = total + el.items.length;
        } else {
          total++;
        }
      });
      return total;
    },
    progressCount() {
      return (this.answers.length / this.totalQuestions) * 100;
    },
    completedSection() {
      return this.totalQuestions === this.answers.length;
    }
  },
  methods: {
    checkVisible(question) {
      return this.lastAnswerIndex >= question.order;
    },
    getAnswer(question) {
      return this.answers.find(el => el.questionId === question.id);
    },
    getParentAnswer(question) {
      return question.parent ? this.answers.find(el => el.questionId === question.parent) : null;
    },
    removeChildren(children) {
      if (!children) { return; }
      for (const childId of children) {
        this.removeAnswer(childId);
      }
    },
    removeAnswer(questionId) {
      this.answers = this.answers.filter(el => el.questionId !== questionId);
      this.$emit('updateAnswers', {
        answers: JSON.parse(JSON.stringify(this.answers)),
        isValid: this.completedSection
      });
    },
    addAnswer(answer, index) {
      const isAnswered = !!this.answers.find(el => el.questionId === answer.questionId);
      let currentQuestion = this.questions.find(el => el.id === answer.questionId);
      if (answer?.multipleParentId) {
        currentQuestion = this.questions.find(el => el.id === answer.multipleParentId)
          .items.find(el => el.id === answer.questionId);
      }
      if (isAnswered) {
        this.answers = this.answers.map((el) => {
          if (el.questionId === answer.questionId) {
            el = answer;
          }
          return el;
        });
        if ((answer.questionId === 4 || answer.questionId === 6) && !answer.value) {
          this.removeAnswer(answer.questionId);
        }
        this.removeChildren(currentQuestion.children);
      } else {
        if (answer.questionId === 1 && !this.isMobile) {
          this.$emit('formStarted');
        }
        this.answers.push(answer);
        if (!answer.multipleParentId || (answer.multipleParentId && answer.questionId !== 3)) {
          const nextQuestion = this.questions[index + 1];
          if (nextQuestion?.order > this.lastAnswerIndex) {
            this.lastAnswerIndex = nextQuestion.order;
          } else if (currentQuestion?.order >= this.lastAnswerIndex) {
            this.lastAnswerIndex = currentQuestion.order + 1;
          }
        }
        if (this.completedSection) {
          this.$emit('trackEvent', {
            eventName: CONSTANTS.cascoPriceShow
          });
        }
        if (this.isMobile && !this.completedSection && answer.questionId !== 8 && answer.questionId !== 9) {
          this.scrollToNext(this.lastAnswerIndex);
        }
      }
      setTimeout(() => {
        this.$emit('updateAnswers', {
          answers: JSON.parse(JSON.stringify(this.answers)),
          isValid: this.completedSection
        });
      }, 200);
      if (this.completedSection && this.isMobile) {
        this.scrollToConfirmation();
      }
    },
    scrollToNext(nextId) {
      setTimeout(() => {
        this.$scrollTo(
          `#question-${nextId}`,
          400,
          {
            offset: -180
          });
      }, 400);
    },
    scrollToConfirmation() {
      setTimeout(() => {
        this.$scrollTo(
          '#confirmation-section',
          400,
          {
            offset: -180
          });
      }, 400);
    },
    updateContactData({ data, isValid }) {
      if (!data) { return; }
      this.contactInfo = JSON.parse(JSON.stringify(data));
      this.validContactInfo = isValid;
      setTimeout(() => {
        this.$emit('updateContactInfo', { data: this.contactInfo, isValid });
      }, 200);
    },
    confirmCasco() {
      this.$emit('confirmCasco');
    }
  }
};
</script>

<style scoped>
.section-progress-stepper {
  position: absolute;
  top: 0;
  left: 0;
}
.question-box {
  width: 100% !important;
  background-color: #E7EFFD;
  padding: 8px;
  border-radius: 4px 4px 4px 4px;
}
@media screen and (max-width: 600px) {
  .confirmation-section {
    width: 100%;
  }
}
</style>
